import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  fullName: string;
  /** Primary role (display + landing). */
  role: string;
  /** All effective roles (primary + additional) — used for role-gated menus. */
  roles?: string[];
  permissions: string[];
  territoryId?: string | null;
  profilePhotoUrl?: string | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  setTokens: (a: string, r: string, u: AuthUser) => void;
  /** Merge fresh fields (role/permissions/…) into the current user without touching tokens. */
  syncUser: (patch: Partial<AuthUser>) => void;
  logout: () => void;
  hasPermission: (code: string) => boolean;
  /** True if the user holds ANY of the given permission codes (empty/undefined ⇒ allowed). */
  hasAnyPermission: (codes?: string[]) => boolean;
  /** True for customer-facing roles that get the shop experience instead of the back-office. */
  isCustomer: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setTokens: (a, r, u) => set({ accessToken: a, refreshToken: r, user: u }),
      syncUser: (patch) => set((s) => (s.user ? { user: { ...s.user, ...patch } } : {})),
      logout: () => set({ accessToken: null, refreshToken: null, user: null }),
      hasPermission: (code: string) => {
        const u = get().user;
        if (!u) return false;
        if (u.role === 'SUPER_ADMIN') return true;
        const perms = u.permissions ?? [];
        return perms.includes(code) || perms.includes('*');
      },
      hasAnyPermission: (codes?: string[]) => {
        if (!codes || codes.length === 0) return true;
        const has = get().hasPermission;
        return codes.some((c) => has(c));
      },
      isCustomer: () => {
        const role = get().user?.role;
        return role === 'RETAILER' || role === 'FARMER';
      },
    }),
    { name: 'gb-auth' },
  ),
);

// --- Reactive permission hooks -------------------------------------------------
// `useAuthStore((s) => s.hasPermission)` subscribes to the *function* (a stable
// reference), so the component never re-renders when permissions change. These
// hooks subscribe to the derived value / the user instead, so gated UI updates
// live the moment a permission is granted or revoked (see useAuthRefresh).

/** Reactive single-permission check — re-renders the caller when it flips. */
export const usePermission = (code: string) => useAuthStore((s) => s.hasPermission(code));

/** Reactive "any of these" check — re-renders the caller when it flips. */
export const useAnyPermission = (codes?: string[]) => useAuthStore((s) => s.hasAnyPermission(codes));

/**
 * Reactive checker for components that test several permissions inline.
 * Subscribes to `user`, so the component re-renders when permissions refresh;
 * the returned function mirrors store.hasPermission.
 */
export const usePermissionChecker = () => {
  const user = useAuthStore((s) => s.user);
  return (code: string) => {
    if (!user) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    const perms = user.permissions ?? [];
    return perms.includes(code) || perms.includes('*');
  };
};
